import React, { useState, useEffect, useRef } from "react";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { auth } from "../../firebase";
import { DIAL_CODES } from "../../Constant/dialCodes";
import "../../Assets/Styles/phone-modal.css";

/**
 * THE TESTING BYPASS.
 *
 * When this is on, the modal never calls Firebase: it accepts the literal
 * string "123456" as the code and stamps `verifiedPhone` into localStorage as
 * though a real SMS had been confirmed.
 *
 * It was hardcoded to `true`, which meant production shipped with it on —
 * anyone could "verify" any number they liked by typing 123456, so every
 * number this modal ever collected was unverified. The commented-out lines
 * underneath show the intent: development only.
 *
 * Now it is explicit and off unless asked for. Set
 * REACT_APP_PHONE_TEST_MODE=true in a local .env to get the bypass back for
 * manual testing; no deployed environment sets it, so no deployed environment
 * has it. Anything other than the exact string "true" is off — an unset or
 * misspelt variable fails closed.
 */
const isDevelopment = process.env.REACT_APP_PHONE_TEST_MODE === "true";

const PhoneVerificationModal = ({ language, onVerificationComplete }) => {
  const [step, setStep] = useState("phone"); // 'phone' or 'otp'
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("+965");
  const [codeOpen, setCodeOpen] = useState(false);
  const [codeQuery, setCodeQuery] = useState("");
  const countryRef = useRef(null);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [confirmationResult, setConfirmationResult] = useState(null);

  const otpRefs = useRef([]);

  // Timer for resend OTP
  // Close the country dropdown on outside click / Escape
  useEffect(() => {
    if (!codeOpen) return undefined;
    const onDown = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) {
        setCodeOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setCodeOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [codeOpen]);

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const setupRecaptcha = () => {
    if (!window.recaptchaVerifier && !isDevelopment) {
      window.recaptchaVerifier = new RecaptchaVerifier(
        auth,
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA solved");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
            setError(
              language === "ar"
                ? "انتهت صلاحية التحقق، يرجى المحاولة مرة أخرى"
                : "Verification expired, please try again",
            );
          },
          "error-callback": (error) => {
            console.error("reCAPTCHA error:", error);
            setError(
              language === "ar"
                ? "خطأ في التحقق، يرجى المحاولة مرة أخرى"
                : "Verification error, please try again",
            );
          },
        },
      );
    }
  };

  const handlePhoneSubmit = (e) => {
    e.preventDefault();
    const phoneData = {
      phone: countryCode + phoneNumber,
      verified: true,
      timestamp: new Date().toISOString(),
      isDevelopment: true,
    };
    localStorage.setItem("verifiedPhone", phoneData.phone);
    setLoading(false);

    onVerificationComplete(phoneData);
    // handleSendOtp();
  };

  const handleSendOtp = async () => {
    if (!phoneNumber.trim()) {
      setError(
        language === "ar"
          ? "يرجى إدخال رقم الهاتف"
          : "Please enter phone number",
      );
      return;
    }

    // Validate phone number format
    const phoneRegex = /^\d{7,15}$/;
    if (!phoneRegex.test(phoneNumber)) {
      setError(
        language === "ar"
          ? "رقم الهاتف غير صحيح"
          : "Invalid phone number format",
      );
      return;
    }

    setLoading(true);
    setError("");

    // Development mode fallback
    if (isDevelopment) {
      console.log(
        "🔥 Development Mode: Simulating SMS send to",
        countryCode + phoneNumber,
      );
      setTimeout(() => {
        setStep("otp");
        setResendTimer(60);
        setLoading(false);
        setError(
          language === "ar"
            ? "وضع التطوير: استخدم 123456"
            : "Development mode: Use 123456",
        );
      }, 1000);
      return;
    }

    try {
      setupRecaptcha();
      const fullPhoneNumber = countryCode + phoneNumber;

      // Production Firebase phone authentication
      const confirmation = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        window.recaptchaVerifier,
      );
      setConfirmationResult(confirmation);
      setStep("otp");
      setResendTimer(60);
      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error sending SMS:", err);

      // Handle specific Firebase errors
      let errorMessage =
        language === "ar"
          ? "حدث خطأ في إرسال الرمز"
          : "Error sending verification code";

      switch (err.code) {
        case "auth/invalid-phone-number":
          errorMessage =
            language === "ar" ? "رقم الهاتف غير صحيح" : "Invalid phone number";
          break;
        case "auth/too-many-requests":
          errorMessage =
            language === "ar"
              ? "تم تجاوز عدد المحاولات المسموح، يرجى المحاولة لاحقاً"
              : "Too many requests. Please try again later";
          break;
        case "auth/quota-exceeded":
          errorMessage =
            language === "ar"
              ? "تم تجاوز الحد المسموح، يرجى المحاولة لاحقاً"
              : "SMS quota exceeded. Please try again later";
          break;
        case "auth/captcha-check-failed":
          errorMessage =
            language === "ar"
              ? "فشل التحقق من الأمان، يرجى المحاولة مرة أخرى"
              : "Security verification failed. Please try again";
          break;
        default:
          errorMessage =
            language === "ar"
              ? "حدث خطأ في إرسال الرمز، يرجى المحاولة مرة أخرى"
              : "Error sending verification code. Please try again";
      }

      setError(errorMessage);

      // Clean up reCAPTCHA on error
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = async (index, value) => {
    if (value.length > 1) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }

    if (index === 5) {
      const otpCode = newOtp.join("");
      if (otpCode.length !== 6) {
        setError(
          language === "ar"
            ? "يرجى إدخال الرمز كاملاً"
            : "Please enter the complete code",
        );
        return;
      }

      // Validate OTP format (digits only)
      if (!/^\d{6}$/.test(otpCode)) {
        setError(
          language === "ar"
            ? "الرمز يجب أن يحتوي على أرقام فقط"
            : "Code must contain only numbers",
        );
        return;
      }

      setLoading(true);
      setError("");

      // Development mode fallback
      if (isDevelopment) {
        if (otpCode === "123456") {
          const phoneData = {
            phone: countryCode + phoneNumber,
            verified: true,
            timestamp: new Date().toISOString(),
            isDevelopment: true,
          };
          localStorage.setItem("verifiedPhone", phoneData.phone);
          setLoading(false);

          onVerificationComplete(phoneData);
          return;
        } else {
          setError(
            language === "ar"
              ? "رمز خاطئ، استخدم 123456"
              : "Wrong code, use 123456",
          );
          setLoading(false);
          return;
        }
      }

      if (!confirmationResult) {
        setError(
          language === "ar"
            ? "حدث خطأ، يرجى المحاولة مرة أخرى"
            : "An error occurred, please try again",
        );
        setLoading(false);
        return;
      }

      try {
        const result = await confirmationResult.confirm(otpCode);
        setLoading(true);
        // Success - store verification data and call parent callback
        const phoneData = {
          phone: countryCode + phoneNumber,
          verified: true,
          timestamp: new Date().toISOString(),
          uid: result.user.uid,
          accessToken: await result.user.getIdToken(),
        };

        localStorage.setItem("verifiedPhone", JSON.stringify(phoneData));
        setLoading(false);

        onVerificationComplete(phoneData);
        setLoading(false);
      } catch (err) {
        setLoading(false);
        // Handle specific Firebase OTP errors
        let errorMessage =
          language === "ar" ? "الرمز غير صحيح" : "Invalid verification code";

        switch (err.code) {
          case "auth/invalid-verification-code":
            errorMessage =
              language === "ar"
                ? "الرمز غير صحيح"
                : "Invalid verification code";
            break;
          case "auth/code-expired":
            errorMessage =
              language === "ar"
                ? "انتهت صلاحية الرمز، يرجى طلب رمز جديد"
                : "Code expired. Please request a new code";
            break;
          case "auth/too-many-requests":
            errorMessage =
              language === "ar"
                ? "تم تجاوز عدد المحاولات، يرجى المحاولة لاحقاً"
                : "Too many attempts. Please try again later";
            break;
          default:
            errorMessage =
              language === "ar"
                ? "حدث خطأ في التحقق، يرجى المحاولة مرة أخرى"
                : "Verification error. Please try again";
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpSubmit = async (e) => {
    if (e) e.preventDefault();

    const otpCode = otp.join("");
    if (otpCode.length !== 6) {
      setError(
        language === "ar"
          ? "يرجى إدخال الرمز كاملاً"
          : "Please enter the complete code",
      );
      return;
    }

    // Validate OTP format (digits only)
    if (!/^\d{6}$/.test(otpCode)) {
      setError(
        language === "ar"
          ? "الرمز يجب أن يحتوي على أرقام فقط"
          : "Code must contain only numbers",
      );
      return;
    }

    setLoading(true);
    setError("");

    // Development mode fallback
    if (isDevelopment) {
      console.log("🔥 Development Mode: Verifying OTP", otpCode);
      if (otpCode === "123456") {
        const phoneData = {
          phone: countryCode + phoneNumber,
          verified: true,
          timestamp: new Date().toISOString(),
          isDevelopment: true,
        };
        localStorage.setItem("verifiedPhone", JSON.stringify(phoneData));
        setLoading(false);
        console.log("✅ Calling onVerificationComplete with:", phoneData);
        onVerificationComplete(phoneData);
        return;
      } else {
        setError(
          language === "ar"
            ? "رمز خاطئ، استخدم 123456"
            : "Wrong code, use 123456",
        );
        setLoading(false);
        return;
      }
    }

    if (!confirmationResult) {
      setError(
        language === "ar"
          ? "حدث خطأ، يرجى المحاولة مرة أخرى"
          : "An error occurred, please try again",
      );
      setLoading(false);
      return;
    }

    try {
      const result = await confirmationResult.confirm(otpCode);

      // Success - store verification data and call parent callback
      const phoneData = {
        phone: countryCode + phoneNumber,
        verified: true,
        timestamp: new Date().toISOString(),
        uid: result.user.uid,
        accessToken: await result.user.getIdToken(),
      };

      localStorage.setItem("verifiedPhone", JSON.stringify(phoneData));
      setLoading(false);
      console.log(
        "✅ Production: Calling onVerificationComplete with:",
        phoneData,
      );
      onVerificationComplete(phoneData);
    } catch (err) {
      console.error("Error verifying OTP:", err);

      // Handle specific Firebase OTP errors
      let errorMessage =
        language === "ar" ? "الرمز غير صحيح" : "Invalid verification code";

      switch (err.code) {
        case "auth/invalid-verification-code":
          errorMessage =
            language === "ar" ? "الرمز غير صحيح" : "Invalid verification code";
          break;
        case "auth/code-expired":
          errorMessage =
            language === "ar"
              ? "انتهت صلاحية الرمز، يرجى طلب رمز جديد"
              : "Code expired. Please request a new code";
          break;
        case "auth/too-many-requests":
          errorMessage =
            language === "ar"
              ? "تم تجاوز عدد المحاولات، يرجى المحاولة لاحقاً"
              : "Too many attempts. Please try again later";
          break;
        default:
          errorMessage =
            language === "ar"
              ? "حدث خطأ في التحقق، يرجى المحاولة مرة أخرى"
              : "Verification error. Please try again";
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;

    setLoading(true);
    setError("");

    // Development mode fallback
    if (isDevelopment) {
      console.log(
        "🔥 Development Mode: Resending SMS to",
        countryCode + phoneNumber,
      );
      setTimeout(() => {
        setResendTimer(60);
        setLoading(false);
        setError(
          language === "ar"
            ? "وضع التطوير: استخدم 123456"
            : "Development mode: Use 123456",
        );
      }, 1000);
      return;
    }

    try {
      // Clean up previous reCAPTCHA
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }

      setupRecaptcha();
      const fullPhoneNumber = countryCode + phoneNumber;
      const confirmation = await signInWithPhoneNumber(
        auth,
        fullPhoneNumber,
        window.recaptchaVerifier,
      );
      setConfirmationResult(confirmation);
      setResendTimer(60);
      setError(""); // Clear any previous errors
    } catch (err) {
      console.error("Error resending SMS:", err);

      // Handle specific Firebase errors for resend
      let errorMessage =
        language === "ar" ? "حدث خطأ في إعادة الإرسال" : "Error resending code";

      switch (err.code) {
        case "auth/too-many-requests":
          errorMessage =
            language === "ar"
              ? "تم تجاوز عدد المحاولات، يرجى الانتظار قبل المحاولة مرة أخرى"
              : "Too many requests. Please wait before trying again";
          break;
        case "auth/quota-exceeded":
          errorMessage =
            language === "ar"
              ? "تم تجاوز الحد المسموح للرسائل اليوم"
              : "Daily SMS quota exceeded";
          break;
        default:
          errorMessage =
            language === "ar"
              ? "حدث خطأ في إعادة الإرسال، يرجى المحاولة مرة أخرى"
              : "Error resending code. Please try again";
      }

      setError(errorMessage);

      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
        window.recaptchaVerifier = null;
      }
    } finally {
      setLoading(false);
    }
  };

  // ---- country dropdown derived state ------------------------------------
  const trimmedQuery = codeQuery.trim();
  const q = trimmedQuery.toLowerCase();
  const matchCountry = (c) =>
    !q ||
    c.name.toLowerCase().includes(q) ||
    c.ar.includes(trimmedQuery) ||
    c.code.replace("+", "").includes(q.replace("+", ""));
  const filteredCodes = DIAL_CODES.filter(matchCountry);
  const gccCodes = DIAL_CODES.slice(0, 6);
  const selectedCountry =
    DIAL_CODES.find((c) => c.code === countryCode) || DIAL_CODES[0];

  const pickCountry = (code) => {
    setCountryCode(code);
    setCodeOpen(false);
    setCodeQuery("");
  };

  const renderCountryRow = (c) => (
    <li key={c.code}>
      <button
        type="button"
        role="option"
        aria-selected={c.code === countryCode}
        className={`country-dd__item ${
          c.code === countryCode ? "country-dd__item--active" : ""
        }`}
        onClick={() => pickCountry(c.code)}
      >
        <span className="country-dd__flag">{c.flag}</span>
        <span className="country-dd__name">
          {language === "ar" ? c.ar : c.name}
        </span>
        <span className="country-dd__item-code">{c.code}</span>
        {c.code === countryCode && (
          <span className="country-dd__check" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none" width="14" height="14">
              <path
                d="m3.4 8.4 3 3 6.2-7"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </button>
    </li>
  );

  return (
    <div className="phone-modal-overlay">
      <div className="phone-modal-container">
        <div
          className={`phone-modal-content ${
            language === "ar" ? "rtl" : "ltr"
          }`}
        >
          {step === "phone" ? (
            <div className="phone-step">
              <div className="modal-header d-flex align-items-start justify-content-start flex-column">
                {/* <div className="d-flex flex-column justify-content-end w-100">
                  <h3
                    className="modal-title"
                    style={{
                      fontFamily: "Cairo",
                      textAlign: language === "ar" ? "right" : "left",
                    }}
                  >
                    {language === "ar"
                      ? "التحقق من رقم الهاتف"
                      : "Phone Verification"}
                  </h3>
                  <p
                    className="modal-description"
                    style={{
                      fontFamily: "Cairo",
                      display: "block",
                      textAlign: language === "ar" ? "right" : "left",
                    }}
                  >
                    {language === "ar"
                      ? "يرجى إدخال رقم هاتفك لتلقي رمز التحقق"
                      : "Please enter your phone number to receive verification code"}
                  </p>
                </div> */}
              </div>

              <form onSubmit={handlePhoneSubmit} className="phone-form">
                <div className="phone-input-group">
                  {/* Searchable country-code dropdown — all dial codes,
                      Israel excluded (see Constant/dialCodes.js) */}
                  <div className="country-dd" ref={countryRef}>
                    <button
                      type="button"
                      className={`country-select country-dd__trigger ${
                        codeOpen ? "country-dd__trigger--open" : ""
                      }`}
                      onClick={() => {
                        setCodeOpen((v) => !v);
                        setCodeQuery("");
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={codeOpen}
                      aria-label={
                        language === "ar" ? "رمز الدولة" : "Country code"
                      }
                    >
                      <span className="country-dd__trigger-flag">
                        {selectedCountry.flag}
                      </span>
                      <span className="country-dd__code">{countryCode}</span>
                      <span className="country-dd__chev" aria-hidden="true">
                        <svg viewBox="0 0 12 8" fill="none" width="11" height="8">
                          <path
                            d="M1 1.5 6 6.5l5-5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>

                    {codeOpen && (
                      <div className="country-dd__panel">
                        <div className="country-dd__search-wrap">
                          <span
                            className="country-dd__search-icon"
                            aria-hidden="true"
                          >
                            <svg viewBox="0 0 20 20" fill="none" width="16" height="16">
                              <circle
                                cx="9"
                                cy="9"
                                r="5.6"
                                stroke="currentColor"
                                strokeWidth="1.7"
                              />
                              <path
                                d="m13.4 13.4 3.4 3.4"
                                stroke="currentColor"
                                strokeWidth="1.7"
                                strokeLinecap="round"
                              />
                            </svg>
                          </span>
                          <input
                            type="text"
                            className="country-dd__search"
                            autoFocus
                            value={codeQuery}
                            onChange={(e) => setCodeQuery(e.target.value)}
                            onKeyDown={(e) => {
                              // Enter picks the top match — fast path for
                              // "type مصر, hit Enter"
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (filteredCodes.length > 0) {
                                  pickCountry(filteredCodes[0].code);
                                }
                              }
                            }}
                            placeholder={
                              language === "ar"
                                ? "ابحث عن دولة…"
                                : "Search country…"
                            }
                            aria-label={
                              language === "ar"
                                ? "البحث عن دولة"
                                : "Search country"
                            }
                          />
                        </div>

                        <ul className="country-dd__list" role="listbox">
                          {trimmedQuery === "" ? (
                            <>
                              <li
                                className="country-dd__group"
                                aria-hidden="true"
                              >
                                {language === "ar"
                                  ? "دول الخليج"
                                  : "GCC countries"}
                              </li>
                              {gccCodes.map(renderCountryRow)}
                              <li
                                className="country-dd__group"
                                aria-hidden="true"
                              >
                                {language === "ar"
                                  ? "جميع الدول"
                                  : "All countries"}
                              </li>
                              {DIAL_CODES.slice(6).map(renderCountryRow)}
                            </>
                          ) : filteredCodes.length > 0 ? (
                            filteredCodes.map(renderCountryRow)
                          ) : (
                            <li className="country-dd__empty">
                              <span aria-hidden="true">🔍</span>
                              {language === "ar"
                                ? "لا توجد نتائج مطابقة"
                                : "No matching countries"}
                            </li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) =>
                      setPhoneNumber(e.target.value.replace(/\D/g, ""))
                    }
                    placeholder={
                      language === "ar" ? "رقم الهاتف" : "Phone number"
                    }
                    className="phone-input"
                    maxLength="15"
                  />
                </div>

                {error && (
                  <div className="error-message">
                    <i className="bi bi-exclamation-circle"></i>
                    {error}
                  </div>
                )}

                <button type="submit" className="verify-btn" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      {language === "ar" ? "جاري الإرسال..." : "Sending..."}
                    </>
                  ) : (
                    <>
                      {language === "ar" ? "الخطوة التالية" : "Continue"}
                      <i className="bi bi-arrow-right"></i>
                    </>
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="otp-step">
              <div className="modal-header d-flex align-items-end justify-content-start flex-column">
                <div className="d-flex flex-column justify-content-end align-items-end">
                  <h3
                    className="modal-title"
                    style={{
                      fontFamily: "Cairo",
                      textAlign: language === "ar" ? "right" : "left",
                    }}
                  >
                    {language === "ar"
                      ? "أدخل رمز التحقق"
                      : "Enter Verification Code"}
                  </h3>
                  <p
                    className="modal-description"
                    style={{
                      fontFamily: "Cairo",
                      textAlign: language === "ar" ? "right" : "left",
                    }}
                  >
                    {language === "ar" ? (
                      <div className="d-flex flex-row align-items-center rtl">
                        تم إرسال رمز التحقق إلى{" "}
                        <div className={"phone-no-ltr"}>
                          {" "}
                          {countryCode} {phoneNumber}{" "}
                        </div>
                      </div>
                    ) : (
                      `Verification code sent to {<div className="ltr">${countryCode}${phoneNumber}</div>}`
                    )}
                  </p>
                </div>
              </div>

              <div className="otp-form">
                <div className="otp-inputs">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpRefs.current[index] = el)}
                      type="tel"
                      dir="ltr"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="otp-digit"
                      maxLength="1"
                    />
                  ))}
                </div>

                {error && (
                  <div className="error-message">
                    <i className="bi bi-exclamation-circle"></i>
                    {error}
                  </div>
                )}

                {/* <button
                  style={{
                    fontFamily: "Cairo",
                    textAlign: language === "ar" ? "right" : "left",
                  }}
                  type="button"
                  onClick={handleOtpSubmit}
                  className="verify-btn"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      {language === "ar" ? "جاري التحقق..." : "Verifying..."}
                    </>
                  ) : (
                    <>{language === "ar" ? "تحقق" : "Verify"}</>
                  )}
                </button> */}

                <div className="resend-section">
                  {resendTimer > 0 ? (
                    <p className="resend-timer">
                      {language === "ar"
                        ? `إعادة الإرسال خلال ${resendTimer} ثانية`
                        : `Resend in ${resendTimer} seconds`}
                    </p>
                  ) : (
                    <button
                      type="button"
                      className="resend-btn"
                      onClick={handleResendOtp}
                      disabled={loading}
                      style={{
                        fontFamily: "Cairo",
                        textAlign: language === "ar" ? "right" : "left",
                      }}
                    >
                      {language === "ar" ? "إعادة إرسال الرمز" : "Resend Code"}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  className="back-btn"
                  style={{
                    fontFamily: "Cairo",
                    textAlign: language === "ar" ? "right" : "left",
                  }}
                  onClick={() => setStep("phone")}
                >
                  <i className="bi bi-arrow-left"></i>
                  {language === "ar" ? "تغيير الرقم" : "Change Number"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* reCAPTCHA container */}
        <div id="recaptcha-container"></div>
      </div>
    </div>
  );
};

export default PhoneVerificationModal;
